import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Pencil, SquareArrowOutUpRight, Sparkles, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { SELECTED_FRAME } from '../../../../components/design-system/common/selection';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDeleteDialog } from '../../../../components/design-system/common/ConfirmDeleteDialog';

interface ProductionCardProps {
    name: string;
    coverUrl?: string;
    subtitle: string;
    onOpen: () => void;
    onRename: (name: string) => Promise<void>;
    onDelete: () => Promise<void>;
    /** Picking several productions at once: whether this one is picked. */
    selected?: boolean;
    /** True while any production is picked — a click then picks instead of opening. */
    selecting?: boolean;
    onToggleSelect?: () => void;
    /** When it was last changed (ISO), shown as "2 hours ago". */
    updatedAt?: string;
    className?: string;
}

/** "2 hours ago", in the page's language. */
export function timeAgo(iso: string | undefined, lang: string): string {
    if (!iso) return '';
    const s = (new Date(iso).getTime() - Date.now()) / 1000;
    if (!Number.isFinite(s)) return '';
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    const steps: Array<[number, Intl.RelativeTimeFormatUnit]> = [[60, 'second'], [60, 'minute'], [24, 'hour'], [7, 'day'], [4.35, 'week'], [12, 'month'], [Infinity, 'year']];
    let v = s;
    for (const [n, unit] of steps) {
        if (Math.abs(v) < n) return rtf.format(Math.round(v), unit);
        v /= n;
    }
    return '';
}

/**
 * One production on the Studio home: its cover, name and what is in it, with a
 * menu to rename it or delete it for good (shots, pictures and clips with it).
 */
export const ProductionCard: React.FC<ProductionCardProps> = ({ name, coverUrl, subtitle, onOpen, onRename, onDelete, selected, selecting, onToggleSelect, updatedAt, className }) => {
    const { t, i18n } = useTranslation();
    const ago = timeAgo(updatedAt, i18n.language || 'en');
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(name);
    const [confirming, setConfirming] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (renaming) { setDraft(name); requestAnimationFrame(() => inputRef.current?.select()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renaming]);

    // While picking, the whole card is the checkbox.
    const activate = () => (selecting && onToggleSelect ? onToggleSelect() : onOpen());

    const commit = async () => {
        const next = draft.trim();
        setRenaming(false);
        if (next && next !== name) await onRename(next);
    };

    return (
        <div className={cn(
            'group relative overflow-hidden rounded-2xl border bg-background transition-[border-color,box-shadow,transform] duration-200',
            selected ? SELECTED_FRAME : 'hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-[0_8px_24px_-14px_rgba(28,25,23,0.25)]',
            deleting && 'pointer-events-none opacity-50',
            className,
        )}>
            {onToggleSelect && (
                <div className={cn(
                    'absolute start-2 top-2 z-10 flex size-7 items-center justify-center rounded-lg bg-background/90 shadow-xs backdrop-blur-sm transition-opacity',
                    selecting || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
                )}>
                    <Checkbox
                        checked={!!selected}
                        onCheckedChange={() => onToggleSelect()}
                        aria-label={t('STUDIO.HOME.SELECT_ONE', 'Select {{name}}', { name })}
                        className="cursor-pointer bg-background"
                    />
                </div>
            )}
            <button type="button" onClick={() => !renaming && activate()} className="block w-full cursor-pointer text-left">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                    {coverUrl
                        ? <img src={coverUrl} alt="" loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                        : <div className="flex size-full items-center justify-center text-muted-foreground"><Sparkles className="size-4" /></div>}
                </div>
            </button>
            <div className="px-3 pb-2.5 pe-9 pt-2">
                {renaming ? (
                    <input
                        ref={inputRef}
                        value={draft}
                        maxLength={120}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commit(); }
                            if (e.key === 'Escape') { setRenaming(false); }
                        }}
                        aria-label={t('STUDIO.HOME.RENAME', 'Rename')}
                        className="-mx-1 w-[calc(100%+0.5rem)] rounded-md border bg-background px-1 text-sm font-medium text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/30"
                    />
                ) : (
                    <button type="button" onClick={activate} className="block w-full cursor-pointer truncate text-left text-sm font-medium text-foreground">{name}</button>
                )}
                <div className="truncate text-xs text-muted-foreground">{subtitle}{ago ? ` · ${ago}` : ''}</div>
            </div>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label={t('STUDIO.HOME.MORE', 'More')}
                        className="absolute bottom-2.5 end-1.5 flex size-7 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground"
                    >
                        <MoreHorizontal className="size-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onSelect={onOpen}><SquareArrowOutUpRight className="size-4" />{t('STUDIO.HOME.OPEN', 'Open')}</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setRenaming(true)}><Pencil className="size-4" />{t('STUDIO.HOME.RENAME', 'Rename')}</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}><Trash2 className="size-4" />{t('STUDIO.HOME.DELETE', 'Delete')}</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ConfirmDeleteDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={t('STUDIO.HOME.DELETE_TITLE', 'Delete “{{name}}”?', { name })}
                description={t('STUDIO.HOME.DELETE_BODY', 'Its shots, photos and videos are deleted with it. This can’t be undone.')}
                confirmLabel={t('STUDIO.HOME.DELETE_CONFIRM', 'Delete production')}
                onConfirm={async () => {
                    setDeleting(true);
                    try { await onDelete(); } finally { setDeleting(false); }
                }}
            />
        </div>
    );
};
